import {
	fetchSmartLinkAttributes,
	type SmartLinkAttributes,
} from '@sosocio/tools/smartlink';
import URL_PARAMETERS from 'settings/url-parameters';

const SMARTLINK_QUERY_PARAMETERS: string[] = [
	'backUrl',
	'backurl',
	'cartUrl',
	'carturl',
	URL_PARAMETERS.country,
	URL_PARAMETERS.countryId,
	URL_PARAMETERS.currency,
	URL_PARAMETERS.customAttributes,
	URL_PARAMETERS.disableOfferingOptions,
	'disabledOptions',
	URL_PARAMETERS.language,
	URL_PARAMETERS.locale,
	URL_PARAMETERS.shopId,
	URL_PARAMETERS.sizeSelect,
	URL_PARAMETERS.skuQuantity,
];

let attributes: SmartLinkAttributes | null = null;
let initialization: Promise<void> | undefined;

/**
 * Discards launch values after their existing consumer has applied them.
 *
 * @param names Attribute names whose values have been consumed.
 */
export function consumeSmartLinkAttributes(names: (keyof SmartLinkAttributes)[]): void {
	for (const name of names) {
		if (attributes) {
			delete attributes[name];
		}
	}
}

/**
 * Reads the successful launch payload without introducing browser persistence.
 *
 * @returns Validated attributes, or null for an absent or failed token lookup.
 */
export function getSmartLinkAttributes(): Readonly<SmartLinkAttributes> | null {
	return attributes;
}

/**
 * Resolves the partner departure destination supplied by the saved launch.
 *
 * @returns An HTTP(S) destination without URL credentials, or undefined.
 */
export function getSmartLinkBackUrl(): string | undefined {
	if (!attributes?.backUrl) {
		return undefined;
	}

	try {
		const url = new URL(
			attributes.backUrl,
			window.location.href,
		);

		if (
			(url.protocol === 'https:' || url.protocol === 'http:')
			&& !url.username
			&& !url.password
		) {
			return url.toString();
		}
	} catch {
		/** An unusable optional destination preserves the existing exit fallback. */
	}

	return undefined;
}

/**
 * Presents saved attributes in the scalar parameter format existing consumers
 * already understand. Successful payloads suppress legacy overrides even when
 * an attribute is omitted or has already been consumed.
 *
 * @param parameters Existing parsed URL or Vue Router query parameters.
 * @returns Parameters with supported SmartLink values sourced from the payload.
 */
export function getSmartLinkParameters<T extends Record<string, unknown>>(parameters: T): T {
	if (!attributes) {
		return parameters;
	}

	const result: Record<string, unknown> = {
		...parameters,
	};

	for (const name of SMARTLINK_QUERY_PARAMETERS) {
		delete result[name];
	}

	const mapped: Record<string, string | undefined> = {
		[URL_PARAMETERS.country]: attributes.country,
		[URL_PARAMETERS.currency]: attributes.currency,
		[URL_PARAMETERS.customAttributes]: attributes.customAttributes === undefined
			? undefined
			: JSON.stringify(attributes.customAttributes),
		[URL_PARAMETERS.disableOfferingOptions]: attributes.productOptions?.enabled === undefined
			? undefined
			: String(!attributes.productOptions.enabled),
		[URL_PARAMETERS.language]: attributes.language,
		[URL_PARAMETERS.shopId]: attributes.shopid?.toString(),
		[URL_PARAMETERS.sizeSelect]: attributes.productOptions?.openOnStart?.toString(),
		[URL_PARAMETERS.skuQuantity]: attributes.quantity?.toString(),
	};

	/** Virtual-SKU launches still forward print limits separately in AppEngine. */
	if (attributes.printColors !== undefined) {
		mapped[URL_PARAMETERS.printColors] = String(attributes.printColors);
	}
	if (attributes.printPositionIndexes !== undefined) {
		mapped[URL_PARAMETERS.printPositions] = attributes.printPositionIndexes.join(',');
	}

	for (const [
		name,
		value,
	] of Object.entries(mapped)) {
		if (value !== undefined) {
			result[name] = value;
		}
	}

	/** Added values are strings, compatible with both existing query formats. */
	return result as T;
}

/**
 * Loads the single-use token before authentication, localization, and routing.
 * Removes the consumed token and duplicated attributes from browser history;
 * failed lookup retains the normal legacy query behavior.
 *
 * @returns Promise settled when the launch source is ready for consumption.
 */
export function initializeSmartLink(): Promise<void> {
	if (initialization) {
		return initialization;
	}

	initialization = (async () => {
		const url = new URL(window.location.href);
		const tokens = url.searchParams.getAll('slt');

		if (!tokens.length) {
			return;
		}

		attributes = tokens.length === 1
			? await fetchSmartLinkAttributes({
				baseUrl: window.glAppUrl,
				token: tokens[0],
			})
			: null;

		url.searchParams.delete('slt');
		if (attributes) {
			for (const name of SMARTLINK_QUERY_PARAMETERS) {
				url.searchParams.delete(name);
			}
			if (attributes.printColors !== undefined) {
				url.searchParams.delete(URL_PARAMETERS.printColors);
			}
			if (attributes.printPositionIndexes !== undefined) {
				url.searchParams.delete(URL_PARAMETERS.printPositions);
			}

			window.shopID = attributes.shopid ?? null;
		}

		window.history.replaceState(
			window.history.state,
			'',
			url,
		);
	})();

	return initialization;
}
